import { ChangeDetectionStrategy, Component, computed, OnDestroy, OnInit, signal, viewChildren, ViewEncapsulation } from '@angular/core';
import { combineLatest, of, take, catchError, switchMap, filter } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ViewportScroller } from '@angular/common';

import { PageSlotModule } from '@spartacus/storefront';

import { AbstractPageComponent } from '@seb/web-common/core/templates/abstract-page/abstract-page.component';
import { IRecipeCard, IRecipeCardWithLayout } from '@seb/web-design-system/recipe-card';
import { DcpListResponse, Recipe, RecipeSearchItem } from '@seb/web-common/core/models';
import { BreadcrumbsComponent } from '@seb/web-common/core/components/static/core';
import { RecipeService, TrackingService } from '@seb/web-common/core/services';
import { ISeo, SdSeoWrapperComponent } from '@seb/web-design-system/seos';
import { CustomJsonLdDirective } from '@seb/web-common/core/directives';
import { toHeading } from '@seb/web-common/core/mappers';
import { BrandsEnum } from '@seb/web-design-system/core';

import { RecipeOverviewComponent } from '../../components/recipe-overview/recipe-overview.component';
import { RecipeReviewsComponent } from '../../components/recipe-reviews/recipe-reviews.component';
import { RecipeSliderComponent } from '../../components/recipe-slider/recipe-slider.component';

@Component({
  selector: 'wc-recipe-details-page',
  templateUrl: './recipe-details-page.component.html',
  styleUrls: ['./recipe-details-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    BreadcrumbsComponent,
    RecipeOverviewComponent,
    PageSlotModule,
    RecipeSliderComponent,
    RecipeReviewsComponent,
    CustomJsonLdDirective,
    SdSeoWrapperComponent
  ]
})
export class RecipeDetailsPageComponent extends AbstractPageComponent implements OnInit, OnDestroy {
  recipeOverviewComponent = viewChildren(RecipeOverviewComponent);
  recipeReviewsComponent = viewChildren(RecipeReviewsComponent);

  hasOverviewLoaded = false;
  hasRecipeReviewLoaded = false;
  brandCorner = BrandsEnum.default;
  useLazyLoad = false;

  similarRecipeCards = signal<IRecipeCardWithLayout[]>([]);
  recipe = signal<Recipe>(undefined);
  snippetRecipes = signal<any>([]);

  seo = computed<ISeo>(() => {
    const recipe = this.recipe();
    return {
      paragraphs: [{
        heading: toHeading({ subline: recipe?.summary })
      }],
      title: recipe?.seoTitle,
      titleLevel: 2
    };
  });

  private maximalSimilarRecipes: number;
  private currentRecipeId: string;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly vps: ViewportScroller,
    private readonly recipeService: RecipeService,
    private readonly trackingService: TrackingService,
    private readonly translateService: TranslateService
  ) {
    super();

    this.snippetsOptions = { images: true, videos: true };
  }

  ngOnInit() {
    super.ngOnInit();

    this.maximalSimilarRecipes = this.computeMaxSimilarRecipes();

    this.subscriptions.add(
      combineLatest([
        this.route.firstChild?.paramMap ?? this.route.paramMap,
        this.route.firstChild?.queryParamMap ?? this.route.queryParamMap
      ]).pipe(
        switchMap(([urlParams, queryParams]) => {
          const recipeSourceSystem: string = urlParams.get('sourceSystem');
          const isTopRecipeId = !queryParams.has('recipeId');
          const recipeId = queryParams.get('recipeId') ?? urlParams.get('recipeId');
          this.currentRecipeId = recipeId;
          return isTopRecipeId
            ? this.recipeService.getFirstRecipeVariant(recipeSourceSystem, recipeId)
            : this.recipeService.getRecipe(recipeSourceSystem, recipeId);
        }),
        catchError(() => {
          this.router.navigate(['/not-found']);
          return of(undefined);
        })
      ).subscribe((recipe: Recipe) => {
        this.recipe.set({
          ...recipe,
          title: recipe?.title?.charAt(0).toUpperCase() + recipe?.title?.slice(1)
        });
        this.initSimilarRecipeCards(recipe);
      })
    );
  }

  onOverviewLoaded() {
    this.hasOverviewLoaded = true;
    this.setRichSnippets();
    this.lazyLoadingService.setUidLoaded('RecipeOverview');
  }

  onRecipeReviewLoaded() {
    this.hasRecipeReviewLoaded = true;
    this.setRichSnippets();
  }

  print(): void {
    this.trackingService.sendEvent('clickPrint', {
      documentType: 'Recipe',
      documentName: this.recipe().title
    });
    window.print();
  }

  scrollToReviews(nbReviews: number): void {
    if (nbReviews) {
      this.trackingService.sendEvent('clickSeeReviews', {
        productName: this.recipe().title,
        productID: this.recipe().fid?.functionalId
      });
      this.vps.scrollToAnchor('reviews');
    }
  }

  onUpdateYield(newYield: number): void {
    const variant = this.recipeService.findVariantFromYield(this.recipe(), newYield);
    if (variant) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { recipeId: variant.fid.functionalId }
      });
    }
  }

  private initSimilarRecipeCards(recipe: Recipe): void {
    this.subscriptions.add(
      this.recipeService.search(recipe?.title, 0, this.maximalSimilarRecipes, {}, '', this.currentRecipeId).pipe(
        filter((searchResult: DcpListResponse<RecipeSearchItem>) => (
          searchResult.content?.length > 0
        )),
        switchMap((searchResult: DcpListResponse<RecipeSearchItem>) => combineLatest(
          searchResult.content.map((item) => this.recipeService.extractRecipeSearchItem(item))
        )),
        take(1)
      ).subscribe((results: IRecipeCard[]) => {
        const recipes = results?.map((result) => ({
          card: result,
          titleLevel: 3
        }));
        this.similarRecipeCards.set(recipes.slice(0, this.maximalSimilarRecipes));
      })
    );
  }

  private computeMaxSimilarRecipes(): number {
    const key = 'recipes.maximalSimilarRecipes';
    const max = this.translateService.instant(key);
    return max !== key ? parseInt(max) : 5;
  }

  private setRichSnippets() {
    this.snippetRecipes.set({
      ...this.recipeOverviewComponent()?.[0]?.snippetsRecipe,
      review: this.recipeReviewsComponent()?.[0]?.snippetsRecipeReview
    });
  }
}
